import { useEffect, useRef } from 'react';
import { generatePath, Navigate, useLocation } from 'react-router-dom';
import { useCreateDialog } from './CreateDialogContext';
import { matchCreateRoute } from './registry';

/**
 * What every create route renders, inside the route's own guards.
 *
 * Where the create dialog exists a create URL is not a page: it is replaced by the page the registry puts behind
 * it, and the dialog is asked for the creation over that page, once, from the URL as it arrived, router state
 * included: it opens there, unless the user keeps a creation something was typed in. The request is the dialog
 * host's and never the landing page's history entry, so reloading that page reopens nothing. Everywhere else
 * the route's page renders.
 */
export function CreateRoutePage() {
    const createDialog = useCreateDialog();
    const location = useLocation();
    // One opening per navigation: this element stays mounted across re-renders of the frame it lives for.
    const openedFor = useRef<string | null>(null);

    useEffect(() => {
        if (createDialog && openedFor.current !== location.key) {
            openedFor.current = location.key;
            createDialog.open(location, location.state);
        }
    }, [createDialog, location]);

    const match = matchCreateRoute(location.pathname);
    if (!match) {
        return null;
    }
    if (!createDialog) {
        return match.route.element;
    }
    return <Navigate to={generatePath(match.route.behind, match.params)} replace />;
}
